import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  useForm,
  useFieldArray,
  useWatch,
  Controller,
  Control,
  UseFormSetValue,
} from "react-hook-form";
import {
  Plus,
  Minus,
  Save,
  X,
  ChevronLeft,
  ChevronRight,
  EyeOff,
  Eye,
} from "lucide-react";
import Input from "../../widgets/Input";
import groupsService from "../../services/groups.service";
import usersService from "../../services/users.service";
import toast, { Toaster } from "react-hot-toast";
import CustomDropdownSelect from "../../widgets/CustomDropdownSelect";
import Tooltip from "../../widgets/ToolTip";
import IconInput from "../../widgets/IconInput";
import inviteusersService from "../../services/invite-users.service";
import Loader from "../../widgets/Loader";

// Interfaces
interface Group {
  group_key: string;
  group_name: string;
  roles: string[];
}

interface UserFormData {
  user_id: string;
  given_name: string;
  family_name: string;
  email: string;
  mobile_no: string;
  password: string;
  confirm_password: string;
  groups: Group[];
}

// Separate interfaces for API calls
interface UserBasicData {
  given_name: string;
  family_name: string;
  email: string;
  mobile_no: string;
  password?: string; // Optional for updates
  user_id?: string;
}

interface Role {
  role_key: string;
}

interface GroupRole {
  group_id: string; // instead of group_key
  roles: Role[]; // array of { role_key }
}

interface UserDepartmentData {
  user_id: string;
  group_roles: GroupRole[];
}

// Props for sub-component
interface GroupRoleFieldsProps {
  control: Control<UserFormData>;
  index: number;
  remove: (index: number) => void;
  departmentList: { value: string; label: string }[];
  totalFields: number;
  setValue: UseFormSetValue<UserFormData>;
}

const GroupRoleFields: React.FC<GroupRoleFieldsProps> = ({
  control,
  index,
  remove,
  departmentList,
  totalFields,
  setValue,
}) => {
  const [roleOptions, setRoleOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [isRolesLoading, setIsRolesLoading] = useState(false);

  const selectedGroup = useWatch({
    control,
    name: `groups.${index}.group_key`,
  });

  const selectedRoles = useWatch({
    control,
    name: `groups.${index}.roles`,
  });

  // Fetch roles when department changes
  useEffect(() => {
    const fetchRoles = async () => {
      if (!selectedGroup) {
        setRoleOptions([]);
        return;
      }
      try {
        setIsRolesLoading(true);
        const response = await usersService.getMappedUsers(selectedGroup);
        const roles =
          response?.data?.map((r: any) => ({
            value: r.role_key,
            label: r.role_name,
          })) || [];

        setRoleOptions(roles);
      } catch (err) {
        console.error("Error fetching roles:", err);
        setRoleOptions([]);
      } finally {
        setIsRolesLoading(false);
      }
    };
    fetchRoles();
  }, [selectedGroup]);

  return (
    <div className="p-4 border border-gray-200 rounded-lg bg-gray-50 space-y-4">
      <div className="flex items-end gap-2">
        {/* Group Dropdown */}
        <div className="flex-1">
          <Controller
            name={`groups.${index}.group_key`}
            control={control}
            rules={{ required: "Group is required" }}
            render={({ field }) => {
              const selectedOption =
                departmentList.find((option) => option.value === field.value) ||
                null;

              return (
                <div>
                  <CustomDropdownSelect
                    label="Select Group"
                    name={field.name}
                    control={control as any}
                    options={departmentList}
                    value={selectedOption}
                    placeholder="Select Group"
                    onSelectionChange={(option: any) => {
                      field.onChange(option ? option.value : "");
                      setValue(
                        `groups.${index}.group_name`,
                        option ? option.label : ""
                      );
                    }}
                  />
                </div>
              );
            }}
          />
        </div>

        {/* Roles Dropdown */}
        <div className="flex-1">
          <Controller
            name={`groups.${index}.roles`}
            control={control}
            rules={{ required: "At least one role is required" }}
            render={({ field }) => {
              const selectedRoleOptions = useMemo(() => {
                return (
                  roleOptions.filter((role) =>
                    selectedRoles?.includes(role.value)
                  ) || []
                );
              }, [roleOptions, selectedRoles]);

              return (
                <div>
                  <CustomDropdownSelect
                    isMulti
                    name={`groups.${index}.roles`}
                    label="Select Roles"
                    control={control as any}
                    placeholder={
                      isRolesLoading ? "Loading roles..." : "Select roles"
                    }
                    options={roleOptions}
                    value={selectedRoleOptions}
                    disabled={!selectedGroup || isRolesLoading}
                    onSelectionChange={(options: any) =>
                      field.onChange(
                        options ? options.map((o: any) => o.value) : []
                      )
                    }
                  />
                </div>
              );
            }}
          />
        </div>

        {totalFields > 1 && (
          <Tooltip content="Remove">
          <button
            type="button"
            onClick={() => remove(index)}
            className="p-2 mb-2 text-red-600 hover:text-red-700"
          >
            <Minus className="w-5 h-5" />
          </button>
            </Tooltip>
        )}
      </div>
    </div>
  );
};

// Modal Component
interface UserFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRefresh: () => void;
  userId?: string;
}

const UserFormModal: React.FC<UserFormModalProps> = ({
  isOpen,
  onClose,
  onRefresh,
  userId,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    getValues,
    clearErrors,
    formState: { isValidating },
    trigger,
  } = useForm<UserFormData>({
    mode: "onChange",
    defaultValues: {
      given_name: "",
      family_name: "",
      email: "",
      mobile_no: "",
      password: "",
      confirm_password: "",
      groups: [{ group_key: "", group_name: "", roles: [] }],
    },
  });

  const [departmentList, setDepartmentList] = useState<
    { value: string; label: string }[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(!!userId);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    fields: groupFields,
    append: addGroup,
    remove: removeGroup,
  } = useFieldArray({
    control,
    name: "groups",
  });

  // Watch password for validation
  const password = watch("password");

  const steps = [
    {
      title: "Basic Information",
      fields: [
        "given_name",
        "family_name",
        "email",
        "mobile_no",
        "password",
        "confirm_password",
      ] as const,
      description: "Enter user's basic details",
    },
    {
      title: "Groups & Role",
      fields: ["groups"] as const,
      description: "Assign groups and roles",
    },
  ];

  // Fetch departments
  useEffect(() => {
    if (!isOpen) return;

    const fetchDepartments = async () => {
      try {
        const response = await groupsService.getGroupsList();
        const departments =
          response?.data?.map((item: any) => ({
            value: item.group_id,
            label: item.group_name,
          })) || [];
        setDepartmentList(departments);
      } catch (err) {
        console.error("Error fetching groups:", err);
        toast.error("Failed to load departments");
      }
    };
    fetchDepartments();
  }, [isOpen]);

  // Fetch user data if editing
  useEffect(() => {
    if (!userId || !isOpen || departmentList.length === 0) return;

    const fetchUser = async () => {
      try {
        setIsInitialLoading(true);
        const response = await usersService.getUserById(userId);
        const data = response.data;

        const formData: UserFormData = {
          user_id: data.user_id,
          given_name: data.name?.givenName || "",
          family_name: data.name?.familyName || "",
          email: data.emails?.[0]?.value || "",
          mobile_no: data.phoneNumbers?.[0]?.value || "",
          password: "", // Don't populate password for security
          confirm_password: "", // Don't populate confirm password
          groups:
            (data?.user_group_map || []).length > 0
              ? data.user_group_map.map((g: any) => ({
                  group_key: g.group_id,
                  group_name:
                    departmentList.find((d) => d.value === g.group_id)?.label ||
                    "",
                  roles: g.roles.map((r: any) => r.role_key),
                }))
              : [{ group_key: "", group_name: "", roles: [] }],
        };

        reset(formData);
      } catch (err) {
        console.error("Error fetching user:", err);
        toast.error("Failed to load user data");
      } finally {
        setIsInitialLoading(false);
      }
    };

    fetchUser();
  }, [userId, isOpen, departmentList]);
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
    }
    if (isOpen && !userId) {
      reset({
        given_name: "",
        family_name: "",
        email: "",
        mobile_no: "",
        password: "",
        confirm_password: "",
        groups: [{ group_key: "", group_name: "", roles: [] }],
      });
    }
  }, [isOpen, userId, reset]);

  const handleNext = useCallback(async () => {
    const currentStepFields = steps[currentStep].fields;

    try {
      const isValid = await trigger(currentStepFields);

      if (isValid) {
        setCurrentStep((prev) => prev + 1);
        if (currentStep + 1 < steps.length) {
          clearErrors(steps[currentStep + 1].fields);
        }
      } else {
        const values = getValues();
        let missingFields: string[] = [];

        if (currentStep === 0) {
          if (!values.given_name) missingFields.push("First Name");
          if (!values.family_name) missingFields.push("Last Name");
          if (!values.email) missingFields.push("Email");
        } else if (currentStep === 1) {
          const hasValidGroup = values.groups?.some(
            (g) => g.group_key && g.roles.length > 0
          );
          if (!hasValidGroup)
            missingFields.push("At least one Department and Role");
        }

        if (missingFields.length > 0) {
          toast.error("Please fill in all required fields");
        }
      }
    } catch (error: any) {
      console.error("Validation error:", error);
      toast.error(
        error?.response?.data?.error?.error_description ||
          "Please check all required fields"
      );
    }
  }, [currentStep, trigger, clearErrors, steps, getValues]);

  const handlePrevious = () => {
    setCurrentStep((prev) => prev - 1);
  };

  // Separate API call functions
  const updateUserBasicInfo = async (
    basicData: UserBasicData
  ): Promise<void> => {
    try {
      await usersService.updateUser(userId!, basicData);
    } catch (err: any) {
      console.error("Error updating user basic info:", err);
      throw new Error(
        err?.response?.data?.error?.error_description ||
          "Failed to update user basic information"
      );
    }
  };

  const updateUserDepartments = async (
    departmentData: UserDepartmentData
  ): Promise<void> => {
    console.log("departmentData", departmentData);
    try {
      await inviteusersService.userGroupMapping(departmentData);
    } catch (err: any) {
      console.error("Error updating user departments:", err);
      throw new Error(
        err?.response?.data?.error?.error_description ||
          "Failed to update user departments"
      );
    }
  };
const createUser = async (userData: UserBasicData): Promise<{ user_id: string }> => {
    try {
        const response = await usersService.createUser(userData);
        return response.data; // should contain user_id
    } catch (err: any) {
        console.error("Error creating user:", err);
        throw new Error(err?.response?.data?.error?.error_description || "Failed to create user");
    }
};

  const onSubmit = async (data: UserFormData) => {
    try {
      setIsLoading(true);

      // Remove confirm_password from data
      const { confirm_password, ...submitData } = data;

      // Filter out empty groups

      const cleanedGroups = submitData.groups
        .filter((g) => g.group_key && g.roles.length > 0) // remove empty groups
        .map((g) => ({
          group_id: g.group_key, // rename group_key → group_id
          roles: g.roles.map((r) => ({ role_key: r })), // convert string → object { role_key }
        }));
      if (userId) {
        console.log("submitData", submitData);

        // EDIT MODE: Separate API calls [web:40][web:45]
        const promises: Promise<void>[] = [];

        // 1. Update basic user information
        const basicData: UserBasicData = {
          given_name: submitData?.given_name,
          family_name: submitData?.family_name,
          email: submitData?.email,
          mobile_no: submitData?.mobile_no,
          user_id: submitData?.user_id,
        };

        // Only include password if it's provided
        if (submitData?.password && submitData?.password.trim() !== "") {
          basicData.password = submitData?.password;
        }

        promises.push(updateUserBasicInfo(basicData));

        // 2. Update user departments/groups separately
        const departmentData: UserDepartmentData = {
          group_roles: cleanedGroups,
          user_id: submitData.user_id,
        };

        promises.push(updateUserDepartments(departmentData));

        // Execute both API calls concurrently [web:40]
        await Promise.all(promises);

        toast.success("User updated successfully!");
      } else {
        const basicData: UserBasicData = {
        given_name: submitData.given_name,
        family_name: submitData.family_name,
        email: submitData.email,
        mobile_no: submitData.mobile_no,
      };

      if (submitData?.password?.trim()) {
        basicData.password = submitData.password;
      }

      const createdUser : any = await createUser(basicData); 
      const user_id = createdUser?.user_info?.user_id || "";
      
      if (cleanedGroups.length > 0) {
        const departmentData: UserDepartmentData = {
          group_roles: cleanedGroups,
          user_id: user_id,
        };

        await updateUserDepartments(departmentData);
      }

      toast.success("User created successfully!");
    }

      onRefresh();
    } catch (err: any) {
      console.error("Error submitting user:", err);
      toast.error(err.message || "An error occurred while saving the user");
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalClose = () => {
    setCurrentStep(0);
    reset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto ">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black bg-opacity-50"
        ></div>

        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[95vh] h-[80vh] flex flex-col">
          <Toaster position="top-right" />

          {/* Modal Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-6">
              <h2 className="text-xl font-semibold text-gray-900">
                {userId ? "Edit User" : "Add User"}
              </h2>

              {/* Stepper */}
              <div className="flex items-center space-x-2">
                {steps.map((step, index) => (
                  <React.Fragment key={index}>
                    <div className="flex items-center">
                      <div
                        className={`flex items-center justify-center w-8 h-8 rounded-full transition-colors ${
                          index <= currentStep
                            ? "bg-blue-600 text-white"
                            : "bg-gray-200 text-gray-600"
                        }`}
                      >
                        {index + 1}
                      </div>
                      <div className="ml-2">
                        <span
                          className={`text-sm font-medium ${
                            index <= currentStep
                              ? "text-blue-600"
                              : "text-gray-500"
                          }`}
                        >
                          {step.title}
                        </span>
                         <p className="text-xs text-gray-400">
                          {step.description}
                        </p>
                      </div>
                    </div>
                    {index < steps.length - 1 && (
                      <div
                        className={`w-8 h-0.5 mx-2 transition-colors ${
                          index < currentStep ? "bg-blue-600" : "bg-gray-200"
                        }`}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            <button
              onClick={handleModalClose}
              type="button"
              className="text-gray-400 hover:text-gray-600 transition-colors p-1"
            >
              <Tooltip content="Close">
                <X className="w-6 h-6" />
              </Tooltip>
            </button>
          </div>

          {/* Modal Body */}
          <div className="p-6 flex-1 overflow-y-auto">
            {isInitialLoading ? (
              <div className="flex items-start justify-center">
            <div className="flex items-center gap-3 mt-[200px] overflow-hidden">
              <Loader />
            </div>
          </div>
            ) : (
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="space-y-6 h-full"
              >
                {/* Step 1: Basic Information */}
                {currentStep === 0 && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      Basic Information
                    </h3>

                    {/* First + Last Name row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input
                        name="given_name"
                        control={control}
                        label="First Name"
                        placeholder="Enter first name"
                        autoComplete="new-given-name"
                        rules={{ 
                          required: "First name is required",
                          pattern: {
                            value: /^[A-Za-z0-9\s]*$/,
                            message: "Only alphabets, numbers, and spaces are allowed"
                          }
                        }}
                      />
                      <Input
                        name="family_name"
                        control={control}
                        label="Last Name"
                        placeholder="Enter last name"
                        autoComplete="new-family-name"
                        rules={{ 
                          required: "Last name is required",
                          pattern: {
                            value: /^[A-Za-z0-9\s]*$/,
                            message: "Only alphabets, numbers, and spaces are allowed"
                          }
                        }}
                      />
                    </div>

                    {/* Email + Mobile row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input
                        name="email"
                        control={control}
                        label="Email"
                        placeholder="Enter email"
                        autoComplete="new-email"
                        rules={{
                          required: "Email is required",
                          pattern: {
                            value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                            message: "Invalid email address",
                          },
                        }}
                      />
                      <Input
                        name="mobile_no"
                        control={control}
                        label="Mobile Number"
                        placeholder="Enter mobile number"
                        type="number"
                        maxLength={10}
                        rules={{
                          pattern: {
                            value: /^[0-9]{10}$/,
                            message: "Mobile number must be 10 digits",
                          },
                        }}
                      />
                    </div>

                    {/* Password + Confirm Password row */}
                    {
                      !userId && (             
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <IconInput
                        name="password"
                        control={control}
                        label="Password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter password"
                        autoComplete="new-password"
                        rules={{
                          required: userId ? false : "Password is required",
                          minLength: {
                            value: 8,
                            message: "Password must be at least 8 characters",
                          },
                          pattern: {
                            value:
                              /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
                            message:
                              "Password must contain uppercase, lowercase, number and special character",
                          },
                        }}
                        suffix={
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="focus:outline-none"
                          >
                            {showPassword ? (
                              <Eye size={18} />
                            ) : (
                              <EyeOff size={18} />
                            )}
                          </button>
                        }
                      />

                      <IconInput
                        name="confirm_password"
                        control={control}
                        label="Confirm Password"
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Confirm password"
                        autoComplete="new-password"
                        rules={{
                          required: userId
                            ? false
                            : "Please confirm your password",
                          validate: (value) =>
                            !password ||
                            !value ||
                            value === password ||
                            "Passwords do not match",
                        }}
                        suffix={
                          <button
                            type="button"
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                            className="focus:outline-none"
                          >
                            {showConfirmPassword ? (
                              <Eye size={18} />
                            ) : (
                              <EyeOff size={18} />
                            )}
                          </button>
                        }
                      />
                    </div>
                      )
                    }

                    {/* Inline Navigation for Step 1 */}
                    <div className="flex justify-end pt-8">
                      <button
                        type="button"
                        onClick={handleNext}
                        disabled={isValidating}
                        className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors mb-5"
                      >
                        {isValidating ? "Validating..." : "Next"}
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 2: Departments & Roles */}
                {currentStep === 1 && (
                  <div className="space-y-6 h-full flex flex-col">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium text-gray-900">
                        Groups & Roles <span className="text-red-500">*</span>
                      </h3>
                      <button
                        type="button"
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium"
                        onClick={() =>
                          addGroup({ group_key: "", group_name: "", roles: [] })
                        }
                      >
                        <Plus className="w-4 h-4" /> Add
                      </button>
                    </div>

                    <div className="flex-1 space-y-4 pr-2 pb-4">
                      {groupFields.map((field, index) => (
                        <GroupRoleFields
                          key={field.id}
                          control={control}
                          index={index}
                          remove={removeGroup}
                          departmentList={departmentList}
                          totalFields={groupFields.length}
                          setValue={setValue}
                        />
                      ))}
                    </div>

                    {groupFields.length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <p>No departments assigned yet.</p>
                        <p className="text-sm">Click "Add" to get started.</p>
                      </div>
                    )}

                    {/* Inline Navigation for Step 2 */}
                    <div className="flex items-center justify-between pt-6 border-t border-gray-200 bg-white pb-4">
                      <div className="flex items-center space-x-3">
                        <button
                          type="button"
                          onClick={handlePrevious}
                          className="flex items-center gap-2 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <ChevronLeft className="w-4 h-4" />
                          Previous
                        </button>
                      </div>

                      <button
                        type="submit"
                        onClick={handleSubmit(onSubmit)}
                        disabled={isLoading}
                        className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                      >
                        <Save className="w-4 h-4" />
                        {isLoading
                          ? "Saving..."
                          : userId
                          ? "Update User"
                          : "Create User"}
                      </button>
                    </div>
                  </div>
                )}
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Usage Example Component
const UserManagement: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editUserId, setEditUserId] = useState<string | undefined>();

  const handleCreateUser = () => {
    setEditUserId(undefined);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditUserId(undefined);
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
        <button
          onClick={handleCreateUser}
          type="button"
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          Create User
        </button>
      </div>

      <UserFormModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onRefresh={() => setIsModalOpen(false)}
        userId={editUserId}
      />
    </div>
  );
};

export default UserFormModal;
export { UserManagement };
